// Pure resolution logic for real-world pick PROTECTIONS and SWAPS, applied during genOrder once
// each pick's draft slot is known. Kept pure (no db/g) so it's unit-testable in isolation; the
// genOrder integration feeds it slots and applies the results.
//
// A pick carries: originalTid (whose record sets the SLOT), tid (the controller who drafts), and
// optionally protection / swap terms (see the DraftPick type).

// Does a protected pick REVERT to its original team? True when its draft slot lands within the
// top-N protected range (e.g. top-4 protected and it drew slot 1-4).
export const protectedPickReverts = (
	slot: number,
	protection: { protectedTop: number },
): boolean => slot <= protection.protectedTop;

// A protected pick that reverts this year still owes the controller in the NEXT eligible season.
// Returns the remaining rollover seasons after consuming the current one (empty => the obligation
// extinguishes / converts, which the caller handles).
export const remainingRolloverSeasons = (
	currentSeason: number,
	rolloverSeasons: number[] | undefined,
): number[] =>
	(rolloverSeasons ?? []).filter((s) => s > currentSeason);

// Given the swap-holder's own slot and the other team's slot, which slot does the holder end up
// with? A favorable swap takes the better (lower-numbered) slot; an unfavorable one the worse.
export const resolveSwapSlot = (
	holderSlot: number,
	otherSlot: number,
	favorable: boolean,
): number =>
	favorable
		? Math.min(holderSlot, otherSlot)
		: Math.max(holderSlot, otherSlot);

// True if a favorable swap would actually CHANGE the holder's slot (i.e. the other pick is
// better). Used to decide whether to swap the two picks' positions.
export const swapImproves = (
	holderSlot: number,
	otherSlot: number,
	favorable: boolean,
): boolean =>
	favorable ? otherSlot < holderSlot : otherSlot > holderSlot;

type ResolvablePick = {
	dpid?: number;
	tid: number;
	originalTid: number;
	round: number;
	pick: number;
	protection?: { protectedTop: number };
	swap?: { swapTid: number; favorable: boolean };
};

export type ObligationChange =
	| { type: "protected"; dpid?: number; revertTo: number; pick: number }
	| { type: "swap"; dpid?: number; otherDpid?: number; newPick: number };

// Apply protections and swaps to a draft's picks once each has a slot (dp.pick). MUTATES the
// picks and returns a log of what changed. Protections resolve first (they change ownership, not
// slots), then swaps (which exchange slots). Only first-round protections are modeled — that's
// where real protections live.
export const applyPickObligations = (
	draftPicks: ResolvablePick[],
): ObligationChange[] => {
	const changes: ObligationChange[] = [];

	// Protections: a protected pick that landed in-range reverts to its original team.
	for (const dp of draftPicks) {
		if (
			dp.protection &&
			dp.round === 1 &&
			dp.tid !== dp.originalTid &&
			protectedPickReverts(dp.pick, dp.protection)
		) {
			changes.push({
				type: "protected",
				dpid: dp.dpid,
				revertTo: dp.originalTid,
				pick: dp.pick,
			});
			dp.tid = dp.originalTid;
		}
	}

	// Swaps: exchange the two picks' slots when the swap improves the holder's position.
	const swapped = new Set<ResolvablePick>();
	for (const dp of draftPicks) {
		if (!dp.swap || swapped.has(dp)) {
			continue;
		}
		const other = draftPicks.find(
			(o) => o.originalTid === dp.swap!.swapTid && o.round === dp.round,
		);
		if (
			other &&
			!swapped.has(other) &&
			swapImproves(dp.pick, other.pick, dp.swap.favorable)
		) {
			const tmp = dp.pick;
			dp.pick = other.pick;
			other.pick = tmp;
			swapped.add(dp);
			swapped.add(other);
			changes.push({
				type: "swap",
				dpid: dp.dpid,
				otherDpid: other.dpid,
				newPick: dp.pick,
			});
		}
	}

	return changes;
};
